import "./LeftNav.css";

import FirstPageOutlinedIcon from "@mui/icons-material/FirstPageOutlined";
import { CssBaseline, Drawer, styled, Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import type { SelectChangeEvent } from "@mui/material/Select";
import type React from "react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { USE_AUTH } from "../../api/apiConfig";
import { usePrefersReducedMotion } from "../../utils/usePrefersReducedMotion";
import { DashboardList } from "./components/DashboardList";
import { GlobalFilter } from "./components/GlobalFilter";
import { LeftPaneLogo } from "./components/LeftPaneLogo";
import { Logout } from "./components/Logout";

const drawerWidth = 288;
const miniDrawerWidth = 56; // width when collapsed (icon-only)
const COLLAPSED_NAV_ROUTE = "/insurance-ar";

const Main = styled("main")(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.enteringScreen,
  }),
  minWidth: 0,
}));

interface NavProps {
  practices: PracticeMessage[];
  selectedPractices: PracticeMessage[];
  selectPractices: (practices: PracticeMessage[]) => void;
  children: React.ReactNode;
}

export default function LeftNav({
  practices,
  selectedPractices,
  selectPractices,
  children,
}: NavProps) {
  const { pathname } = useLocation();
  const isCollapsedRoute = pathname.startsWith(COLLAPSED_NAV_ROUTE);
  const [isNavOpen, setNavOpen] = useState<boolean>(!isCollapsedRoute);
  const prefersReducedMotion = usePrefersReducedMotion();

  // Only fires when the route crosses the A/R boundary, so a manual toggle sticks.
  useEffect(() => {
    setNavOpen(!isCollapsedRoute);
  }, [isCollapsedRoute]);

  const onClickNav = () => {
    setNavOpen(!isNavOpen);
  };

  const handlePracticeChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    const practiceIdValues =
      typeof value === "string" ? value.split(",") : value;

    const updatedPractices = practiceIdValues.reduce((acc, wieldyId) => {
      const match = practices.find(
        (practice) => practice.wieldyId === wieldyId,
      );
      if (match) {
        acc.push(match);
      }
      return acc;
    }, [] as PracticeMessage[]);

    selectPractices(updatedPractices);
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", width: "100%" }}>
      <CssBaseline />
      <Drawer
        sx={{
          width: isNavOpen ? `${drawerWidth}px` : `${miniDrawerWidth}px`,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: isNavOpen ? `${drawerWidth}px` : `${miniDrawerWidth}px`,
            boxSizing: "border-box",
            overflow: "hidden",
            transition: prefersReducedMotion
              ? "none"
              : "width 0.3s ease-in-out",
          },
        }}
        PaperProps={{
          style: {
            backgroundColor: "#25233F",
            borderRadius: "0px 24px 24px 0px",
            padding: isNavOpen ? "10px 15px 10px 15px" : "10px 4px",
          },
        }}
        variant="permanent"
        anchor="left"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Tooltip title={isNavOpen ? "" : "Expand"} placement="right">
            <IconButton
              aria-label={isNavOpen ? "Close Pane" : "Open Pane"}
              onClick={onClickNav}
              sx={{
                width: "fit-content",
                marginLeft: "auto",
                paddingBottom: 0,
              }}
            >
              <FirstPageOutlinedIcon
                sx={{
                  border: "solid 2px white",
                  color: "white",
                  borderRadius: "6px",
                  transform: isNavOpen ? "rotate(0deg)" : "rotate(180deg)",
                  transition: prefersReducedMotion
                    ? ""
                    : "transform 0.5s ease-in-out",
                }}
              />
            </IconButton>
          </Tooltip>
          {isNavOpen && <LeftPaneLogo />}
          {isNavOpen && (
            <GlobalFilter
              selectValues={selectedPractices}
              values={practices}
              label="Practice"
              onChange={handlePracticeChange}
            />
          )}
          {isNavOpen && (
            <Box
              component="hr"
              sx={{
                width: "90%",
                height: "1px",
                backgroundColor: "#E9D7D9",
                border: "none",
                margin: ".25rem 0 2rem 3px",
              }}
            />
          )}
          {!isNavOpen && <Box sx={{ height: "1rem" }} />}
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            <div className="navigation">
              <nav>
                <DashboardList practices={practices} isNavOpen={isNavOpen} />
              </nav>
            </div>
          </Box>
          {USE_AUTH && <Logout isNavOpen={isNavOpen} />}
        </Box>
      </Drawer>
      <Main>{children}</Main>
    </Box>
  );
}
